import { Component, OnInit, ViewChild } from '@angular/core';
import { catchError, forkJoin, of, take } from 'rxjs';
import { ColDef, GridApi, GridOptions, GridReadyEvent, IServerSideDatasource, IServerSideGetRowsParams, SideBarDef } from 'ag-grid-community';
import { tkColDef, tkGridOptions, tkSideBar } from 'src/app/shared/ag-grid/tk-grid';
import { ShipmentService } from 'src/app/services/modules/shipments/shipment.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { SavedSearchComponent } from 'src/app/components/saved-search/saved-search.component';
import { buildShipmentGridQuery, shipmentGridRows, shipmentGridTotal } from '../shipment-grid-query';

type StatusOption = { label: string; value: string };
type CategoryOption = { label: string; value: string };

// Map dashboard status filter to grid set-filter values.
const DASHBOARD_STATUS: Record<string, string[]> = {
  complete: [
    'complete',
  ],
  'in-transit': [
    'in_transit',
    'customer_collected',
    'provider_collected',
  ],
  booked: [
    'booked',
  ],
};

@Component({
  selector: 'app-shipment-bookings',
  templateUrl: './shipment-bookings.component.html',
  styleUrls: [
    './shipment-bookings.component.scss',
  ],
  providers: [
    ConfirmDialogComponent,
  ],
})
export class ShipmentBookingsComponent implements OnInit {
  /** Toolbar mirror of the grid `status` set-filter (Financial pattern). */
  statusSelected: string[] = [];
  statusOptions: StatusOption[] = [];
  statusSearch = '';
  /** Category set-filter options from GET freight/categories (value = url_key). */
  private categoryOptions: CategoryOption[] = [];

  viewFilterType = 'booking';
  module = 'shipment';
  /** Checkbox stays left; Action is pinned right in colDefs (do not list it here — saved views force these left). */
  readonly pinnedColumns = [
    '0',
  ];

  paginationPageSize = 20;
  private readonly maxPageSize = 200;

  fromDashboard = false;
  pendingDashboardFilter?: string;

  @ViewChild('savedSearch') savedSearch!: SavedSearchComponent;

  gridApi?: GridApi;
  customerId: any = null;
  customerDetails: any;
  private statusesReady = false;
  private categoriesReady = false;
  private gridReady = false;
  private bootstrapped = false;

  columnDefs: ColDef[] = this.shipmentService.transformBookingColumnV2();
  readonly defaultColDef: ColDef = tkColDef({
    width: 130,
    filter: true,
    sortable: true,
  });
  readonly sideBar: SideBarDef = tkSideBar('filters');
  readonly gridOptions: GridOptions = {
    ...tkGridOptions({
      rowModelType: 'serverSide',
      pageSize: this.paginationPageSize,
      pageSizeOptions: [
        10,
        20,
        50,
        100,
      ],
      getRowId: (params) => String(params.data?.booking_id ?? params.data?.listing_id ?? ''),
      sideBar: this.sideBar,
    }),
    suppressRowClickSelection: true,
    rowSelection: 'multiple',
    overlayNoRowsTemplate: '<span class="ship-empty">No bookings to show.</span>',
  };

  constructor(
    private shipmentService: ShipmentService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.authService.getUser().subscribe((res: any) => {
      if (res.status == 'success') {
        this.customerId = res.data.customer_id;
        this.customerDetails = res.data;
      }
    });
    this.loadFilterOptions();
  }

  /** "Status", the one picked label, or "Status (n)". */
  get statusLabel(): string {
    const picked = this.statusSelected;
    if (picked.length === 0) {
      return 'Status';
    }
    if (picked.length === 1) {
      return this.statusOptions.find((option) => option.value === picked[0])?.label ?? picked[0];
    }
    return `Status (${picked.length})`;
  }

  get filteredStatusOptions(): StatusOption[] {
    const query = this.statusSearch.trim().toLowerCase();
    if (!query) {
      return this.statusOptions;
    }
    return this.statusOptions.filter((option) => option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query));
  }

  onStatusMenuOpenChange(open: boolean): void {
    if (!open) {
      this.statusSearch = '';
    }
  }

  private loadFilterOptions(): void {
    // Per-stream catch so one options API failure does not clear the other set.
    forkJoin({
      statuses: this.shipmentService.fetchStatus(this.viewFilterType).pipe(
        take(1),
        catchError(() => of({ data: [] })),
      ),
      categories: this.shipmentService.getFreightCategories().pipe(
        take(1),
        catchError(() => of({ data: [] })),
      ),
    }).subscribe({
      next: ({ statuses, categories }) => {
        this.statusOptions = (statuses?.data ?? []).filter((s: any) => s?.value != null && s.value !== '').map((s: any) => ({ label: s.name ?? s.value, value: String(s.value) }));
        this.applyStatusFilterValues();
        this.statusesReady = true;

        this.categoryOptions = (categories?.data ?? []).filter((c: any) => c?.url_key).map((c: any) => ({ label: String(c.name ?? c.url_key), value: String(c.url_key) }));
        this.applyCategoryFilterValues();
        this.categoriesReady = true;

        this.checkFromDashboard();
        void this.bootstrapGrid();
      },
      error: () => {
        this.statusesReady = true;
        this.categoriesReady = true;
        this.checkFromDashboard();
        void this.bootstrapGrid();
      },
    });
  }

  /** Apply booking status API options to the Status column set-filter (and keep toolbar labels in sync). */
  private applyStatusFilterValues(): void {
    const values = this.statusOptions.map((option) => option.value);
    const labelByValue = new Map(
      this.statusOptions.map((option) => [
        option.value,
        option.label,
      ]),
    );
    const statusCol = this.columnDefs.find((col) => col.colId === 'status');
    if (statusCol) {
      statusCol.filter = 'agSetColumnFilter';
      statusCol.filterParams = {
        ...(statusCol.filterParams ?? {}),
        values,
        valueFormatter: (p: { value: string | null }) => labelByValue.get(p.value ?? '') ?? p.value ?? '',
      };
    }
  }

  /** Category set-filter: send url_key; show name in the filter UI. */
  private applyCategoryFilterValues(): void {
    const values = this.categoryOptions.map((option) => option.value);
    const labelByValue = new Map(
      this.categoryOptions.map((option) => [
        option.value,
        option.label,
      ]),
    );
    const categoryCol = this.columnDefs.find((col) => col.colId === 'category');
    if (categoryCol) {
      categoryCol.filter = 'agSetColumnFilter';
      categoryCol.filterParams = {
        ...(categoryCol.filterParams ?? {}),
        values,
        valueFormatter: (p: { value: string | null }) => labelByValue.get(p.value ?? '') ?? p.value ?? '',
        refreshValuesOnOpen: true,
      };
    }
  }

  /**
   * One SSRM attach after statuses + categories + saved views are ready.
   * Avoids: empty fetch → setFilterModel → second fetch (and columnDefs churn).
   */
  private async bootstrapGrid(): Promise<void> {
    if (this.bootstrapped || !this.gridReady || !this.statusesReady || !this.categoriesReady || !this.gridApi || this.gridApi.isDestroyed()) {
      return;
    }
    this.bootstrapped = true;

    this.gridApi.setGridOption('columnDefs', this.columnDefs);
    this.ensureActionPinnedRight();

    if (this.savedSearch) {
      await this.savedSearch.refresh(this.gridApi);
    }

    if (this.pendingDashboardFilter) {
      this.applyPendingDashboardFilter();
    }

    this.gridApi.setGridOption('serverSideDatasource', this.createDatasource());
  }

  /** Saved views used to force Action left; keep it stuck on the right. */
  private ensureActionPinnedRight(): void {
    if (!this.gridApi || this.gridApi.isDestroyed()) {
      return;
    }
    this.gridApi.applyColumnState({
      state: [
        { colId: 'action', pinned: 'right' },
      ],
    });
  }

  private checkFromDashboard(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['filter'] !== undefined) {
        this.fromDashboard = true;
        this.pendingDashboardFilter = String(params['filter']).toLowerCase();
        this.clearUrlParams();
        if (this.savedSearch) {
          this.savedSearch.clearFilter();
        }
        if (this.gridApi && !this.gridApi.isDestroyed()) {
          this.applyPendingDashboardFilter();
        }
      }
    });
  }

  clearUrlParams(): void {
    this.router.navigate(
      [
        '.',
      ],
      { relativeTo: this.route, queryParams: {} },
    );
  }

  private applyPendingDashboardFilter(): void {
    const filterKey = this.pendingDashboardFilter;
    this.pendingDashboardFilter = undefined;
    if (!filterKey) {
      return;
    }
    const mapped = DASHBOARD_STATUS[filterKey];
    this.setStatuses(
      mapped
        ? [
            ...mapped,
          ]
        : [],
    );
    this.fromDashboard = false;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setSideBarVisible(true);
    this.gridReady = true;
    void this.bootstrapGrid();
  }

  onPaginationChanged(): void {
    if (!this.gridApi || this.gridApi.isDestroyed()) {
      return;
    }
    let newPageSize = this.gridApi.paginationGetPageSize();
    if (newPageSize > this.maxPageSize) {
      newPageSize = this.maxPageSize;
      this.gridApi.setGridOption('paginationPageSize', newPageSize);
    }
    if (newPageSize === this.paginationPageSize) {
      return;
    }
    this.paginationPageSize = newPageSize;
    this.gridApi.setGridOption('cacheBlockSize', newPageSize);
    this.gridApi.refreshServerSide({ purge: true });
  }

  private createDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const query = buildShipmentGridQuery({
          startRow: params.request.startRow ?? 0,
          endRow: params.request.endRow ?? this.paginationPageSize,
          sortModel: params.request.sortModel,
          filterModel: params.request.filterModel as Record<string, any> | null,
          maxPageSize: this.maxPageSize,
        });

        this.shipmentService
          .getCustomerBookingV2(query)
          .pipe(take(1))
          .subscribe({
            next: (res: any) => {
              const rows = shipmentGridRows(res);
              const total = shipmentGridTotal(res);
              params.success({ rowData: rows, rowCount: total });
              if (total === 0) {
                this.gridApi?.showNoRowsOverlay();
              } else {
                this.gridApi?.hideOverlay();
              }
            },
            error: () => {
              params.fail();
              this.gridApi?.showNoRowsOverlay();
            },
          });
      },
    };
  }

  clearFiltersExternally(): void {
    this.statusSelected = [];
  }

  /** Toolbar stays in step when the status column / side bar / clear-button edits the same filter. */
  onFilterChanged(): void {
    const status = this.gridApi?.getFilterModel()?.['status'];
    this.statusSelected = status?.filterType === 'set' && Array.isArray(status.values) ? status.values.map(String) : [];
  }

  toggleStatus(value: string): void {
    const picked = this.statusSelected.includes(value)
      ? this.statusSelected.filter((status) => status !== value)
      : [
          ...this.statusSelected,
          value,
        ];
    this.setStatuses(picked);
  }

  clearStatuses(): void {
    this.setStatuses([]);
  }

  private setStatuses(values: string[]): void {
    this.statusSelected = values;
    const model = values.length ? { filterType: 'set', values } : null;
    void this.gridApi?.setColumnFilterModel('status', model).then(() => this.gridApi?.onFilterChanged());
  }
}
